// Top-level org panel menus — must match useSideBarList entries.
export const ORGANIZATION_MENU_KEYS = [
    "reports",
    "messages",
    "survey",
    "settings",
    "customer_club",
    "restaurant",
];

// Older configs may still carry these; sidebar folds them into parent menus.
export const LEGACY_ORGANIZATION_MENU_ALIASES = {
    market: "restaurant",
    points: "customer_club",
};

export const createDefaultSidebarConfig = () =>
    ORGANIZATION_MENU_KEYS.reduce(
        (config, key) => ({
            ...config,
            [key]: { enabled: true, label: "" },
        }),
        {}
    );

export const createEmptyMenuAccess = () =>
    ORGANIZATION_MENU_KEYS.reduce(
        (config, key) => ({
            ...config,
            [key]: { enabled: false },
        }),
        {}
    );

/** Fold legacy market/points into restaurant/customer_club for form state. */
export const normalizeMenuConfig = (config, { withLabels = true } = {}) => {
    const source = config && typeof config === "object" ? { ...config } : {};
    Object.entries(LEGACY_ORGANIZATION_MENU_ALIASES).forEach(
        ([legacyKey, targetKey]) => {
            const legacy = source[legacyKey];
            delete source[legacyKey];
            if (!legacy || typeof legacy !== "object") return;
            const target = source[targetKey];
            const enabled =
                (target && target.enabled === true) || legacy.enabled === true;
            if (withLabels) {
                const label = (
                    (target && target.label) ||
                    legacy.label ||
                    ""
                ).trim();
                source[targetKey] = { enabled, label };
            } else {
                source[targetKey] = { enabled };
            }
        }
    );
    return source;
};

export const applyPackageMenusToSidebar = (sidebarConfig, menuAccess) => {
    const next = {
        ...createDefaultSidebarConfig(),
        ...normalizeMenuConfig(sidebarConfig, { withLabels: true }),
    };
    ORGANIZATION_MENU_KEYS.forEach((key) => {
        next[key] = {
            enabled: menuAccess?.[key]?.enabled === true,
            label: next[key]?.label || "",
        };
    });
    return next;
};

export const getOrganizationMenuItem = (
    auth,
    key,
    defaultTitle,
    legacyEnabled
) => {
    const config = auth.sidebar_config;
    const configured =
        config &&
        Object.prototype.hasOwnProperty.call(config, key) &&
        typeof config[key] === "object";

    if (!configured) {
        return {
            enabled: Boolean(legacyEnabled),
            title: defaultTitle,
        };
    }

    const customLabel = (config[key].label || "").trim();
    // Organization menu config takes priority over package flags.
    return {
        enabled: config[key].enabled === true,
        title: customLabel || defaultTitle,
    };
};

export const isOrganizationMenuEnabled = (auth, key, legacyEnabled) =>
    getOrganizationMenuItem(auth, key, "", legacyEnabled).enabled;

export const PARTICIPATION_PERMISSION_KEYS = [
    "club_permission",
    "content_permission",
    "incentive_permission",
];

export const isParticipationPackageEnabled = (pkg) =>
    PARTICIPATION_PERMISSION_KEYS.some((key) => Boolean(pkg?.[key]));

/** Org has participation management, so every inner tab is available. */
export const isParticipationEnabled = (auth) =>
    Boolean(auth?.company_admin) &&
    isOrganizationMenuEnabled(
        auth,
        "customer_club",
        isParticipationPackageEnabled(auth?.package)
    );
